/**
 * Browser Steps Viewer Component
 *
 * Displays streaming agent steps from a browser farm instance via WebSocket.
 * Connects to the agent stream endpoint and renders each step using BrowserStep,
 * newest first so the step in progress sits directly under the task card.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn, Flex, ScrollArea, Spinner, Text } from '@kognitos/lattice';

import { EAgentStatus, EBrowserStepStatus, type IAgentStep } from '../types';
import { useAgentStream } from '../hooks/use-agent-stream';
import { useMetadataSteps } from '../hooks/use-metadata-steps';
import { formatPlaybackTime } from '../utils/format-playback-time';
import { isStepReached } from '../utils/step-offset';
import { getStepTypeFromAction } from '../utils/step-type';
import { BrowserStep } from './BrowserStep';

/**
 * Props for the BrowserStepsViewer component.
 *
 * Two data sources, mutually exclusive:
 *  - `streamUrl` (live): SSE step stream from the executor's /stream endpoint.
 *  - `artifactsBase` (replay): metadata.json fetched once from the session's
 *    artifacts URL. Use {@link resolveArtifactsBaseUrl} to derive from a
 *    `liveViewUrl`.
 *
 * When both are non-null, `artifactsBase` wins (replay supersedes live).
 */
export interface IBrowserStepsViewerProps {
  /** SSE URL for the agent stream endpoint (live mode). Pass null to skip. */
  streamUrl?: string | null;
  /** Artifacts base URL for the session's metadata.json (replay mode). Pass null to skip. */
  artifactsBase?: string | null;
  /** Maximum number of steps to retain. Live mode only. Defaults to 500. */
  maxSteps?: number;
  /** Additional CSS classes. */
  className?: string;
  /** When true, the browser session has ended (pod deleted). Skips connection retries for a snappier UX. */
  sessionEnded?: boolean;
  /**
   * Called when the user clicks a step. The parent typically uses this to
   * seek the replay video (`step.video_offset_seconds`) in replay mode.
   * Live mode passes a no-op callback or omits.
   */
  onStepClick?: (step: IAgentStep) => void;
  /**
   * Replay-mode only: current playback position of the `<video>` element
   * in seconds. The viewer derives the active step internally as the step
   * whose `video_offset_seconds` is the largest value ≤ `activeStepTime`.
   * Parent doesn't need to know the steps list to compute this.
   */
  activeStepTime?: number;
}

/**
 * BrowserStepsViewer displays streaming agent steps from a browser farm WebSocket.
 *
 * It connects to the agent stream endpoint, receives steps in real-time,
 * and renders them using the BrowserStep component inside a ScrollArea.
 * Auto-scrolls to the latest step as they arrive.
 *
 * @param props - Component props
 * @returns BrowserStepsViewer component
 *
 * @example
 * <BrowserStepsViewer
 *   streamUrl="ws://bf.example.com/agent/stream/browser-123"
 * />
 */
export function BrowserStepsViewer({
  streamUrl,
  artifactsBase,
  maxSteps,
  className,
  sessionEnded = false,
  onStepClick,
  activeStepTime,
}: IBrowserStepsViewerProps) {
  'use no memo';
  const { t } = useTranslation('browser-automation');
  // Replay supersedes live. When in replay mode, `streamUrl` is forced null
  // for useAgentStream so it doesn't open an SSE connection alongside the
  // metadata fetch.
  const isReplay = !!artifactsBase;
  const liveSource = useAgentStream(isReplay ? null : (streamUrl ?? null), {
    maxSteps,
  });
  const replaySource = useMetadataSteps(isReplay ? artifactsBase : null);
  const { steps, agentStatus, isConnecting, isConnected, error } = isReplay
    ? replaySource
    : liveSource;

  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const isAgentRunning =
    agentStatus === EAgentStatus.RUNNING || agentStatus === null;

  const orderedSteps = useMemo(() => [...steps].reverse(), [steps]);

  // The live stream carries no session start, so the earliest step is 0:00.
  const liveStartMs = useMemo(() => {
    const times = steps
      .map(step => Date.parse(step.timestamp))
      .filter(Number.isFinite);
    return times.length > 0 ? Math.min(...times) : null;
  }, [steps]);

  /** Elapsed `m:ss` label: recording offset in replay, time since the first step live. */
  const getStepTime = (step: IAgentStep): string | undefined => {
    if (isReplay) {
      return step.video_offset_seconds != null
        ? formatPlaybackTime(step.video_offset_seconds)
        : undefined;
    }
    const stepMs = Date.parse(step.timestamp);
    if (liveStartMs == null || !Number.isFinite(stepMs)) return undefined;
    return formatPlaybackTime((stepMs - liveStartMs) / 1000);
  };
  const latestStep = steps[steps.length - 1];

  // Live mode: newest steps render at the top, so bring the viewport back up
  // when one arrives. Replay scrolling is handled by the next effect.
  // Uses scrollTo on the ScrollArea viewport instead of scrollIntoView,
  // which would scroll ALL ancestor containers (including the browser player).
  // eslint-disable-next-line no-restricted-syntax -- reason: imperative scroll on DOM ref
  useEffect(() => {
    if (isReplay) return;
    const viewport = scrollAreaRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]',
    );
    if (viewport) {
      viewport.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [steps, isReplay]);

  /**
   * In replay mode, the active step is the latest one whose
   * `video_offset_seconds` <= `activeStepTime`. Computed here (rather than
   * in the parent) because we own the steps list. Full linear scan — no
   * early break, so we don't depend on the metadata steps being ordered
   * by offset. Even sessions with hundreds of steps render in under a ms.
   */
  const activeStepNumber = useMemo<number | null>(() => {
    if (!isReplay || activeStepTime == null) return null;
    let active: number | null = null;
    let activeOffset = -Infinity;
    for (const step of steps) {
      const offset = step.video_offset_seconds;
      if (offset == null) continue;
      if (isStepReached(offset, activeStepTime) && offset >= activeOffset) {
        active = step.step_number;
        activeOffset = offset;
      }
    }
    return active;
  }, [isReplay, steps, activeStepTime]);

  // Replay mode: keep the active step visible as the recording plays.
  // The active highlight is driven by `activeStepNumber`; when it changes
  // we scroll the matching `data-step-number` element into view if it
  // has drifted outside the ScrollArea viewport. Uses offset math rather
  // than `scrollIntoView` so only the viewport scrolls — not its
  // ancestors (the dialog, page, etc.).
  // eslint-disable-next-line no-restricted-syntax -- reason: imperative scroll on DOM ref
  useEffect(() => {
    if (!isReplay || activeStepNumber == null) return;
    const viewport = scrollAreaRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]',
    ) as HTMLElement | null;
    if (!viewport) return;
    const el = viewport.querySelector<HTMLElement>(
      `[data-step-number="${activeStepNumber}"]`,
    );
    if (!el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    const visTop = viewport.scrollTop;
    const visBottom = visTop + viewport.clientHeight;
    if (top < visTop) {
      viewport.scrollTo({ top, behavior: 'smooth' });
    } else if (bottom > visBottom) {
      viewport.scrollTo({
        top: bottom - viewport.clientHeight,
        behavior: 'smooth',
      });
    }
  }, [isReplay, activeStepNumber]);

  /**
   * Determines the BrowserStep status for a given step.
   *
   * Live mode: the latest step is CURRENT while the agent is still running.
   * Replay mode: the step whose number matches `activeStepNumber` is CURRENT.
   * Other steps in replay are always COMPLETE — the recording is over.
   */
  const getStepStatus = useCallback(
    (step: IAgentStep): EBrowserStepStatus => {
      if (isReplay) {
        return activeStepNumber != null && step.step_number === activeStepNumber
          ? EBrowserStepStatus.CURRENT
          : EBrowserStepStatus.COMPLETE;
      }
      return isAgentRunning && step === latestStep
        ? EBrowserStepStatus.CURRENT
        : EBrowserStepStatus.COMPLETE;
    },
    [isReplay, isAgentRunning, latestStep, activeStepNumber],
  );

  // Session ended with no cached steps — show fallback immediately
  // instead of "Connecting..." while useAgentStream retries against a dead pod.
  if (sessionEnded && steps.length === 0 && !isConnected) {
    return (
      <Flex
        align="center"
        justify="center"
        className={cn('h-full w-full', className)}
      >
        <Text level="small" color="muted" className="text-center px-4">
          {t('browserAutomation.steps.unavailable')}
        </Text>
      </Flex>
    );
  }

  // Connecting state — only show spinner when there are no cached steps to display
  if (isConnecting && steps.length === 0) {
    return (
      <Flex
        align="center"
        justify="center"
        className={cn('h-full w-full', className)}
      >
        <Flex direction="column" align="center" gap={2}>
          <Spinner size="sm" />
          <Text level="small" color="muted">
            {t('browserAutomation.steps.connecting')}
          </Text>
        </Flex>
      </Flex>
    );
  }

  // Error state — only when no steps to show
  if (error && steps.length === 0) {
    return (
      <Flex
        align="center"
        justify="center"
        className={cn('h-full w-full', className)}
      >
        <Text level="small" color="muted" className="text-center px-4">
          {error}
        </Text>
      </Flex>
    );
  }

  // Empty state (connected but no steps yet)
  if (steps.length === 0) {
    return (
      <Flex
        align="center"
        justify="center"
        className={cn('h-full w-full', className)}
      >
        <Text level="small" color="muted">
          {t('browserAutomation.steps.empty')}
        </Text>
      </Flex>
    );
  }

  // Steps list (may have error too, shown as footer)
  return (
    <ScrollArea
      ref={scrollAreaRef}
      className={cn('flex-1 min-h-0 w-full', className)}
    >
      <Flex direction="column" gap={2}>
        {orderedSteps.map(step => {
          const status = getStepStatus(step);
          // Click-to-seek only makes sense for replay steps that carry a
          // video offset. Live steps don't have one; the parent wouldn't
          // have a video to seek anyway.
          const seekable = isReplay && step.video_offset_seconds != null;
          // The wrapping div carries `data-step-number` so the replay
          // auto-scroll effect can locate the active step in the DOM.
          // `w-full` is required: without it the div collapses to its
          // content width (Lattice's <Flex> doesn't stretch children by
          // default), which in turn makes BrowserStep's own `w-full`
          // resolve against a too-narrow parent.
          return (
            <div
              key={`${step.step_number}-${step.timestamp}`}
              data-step-number={step.step_number}
              className="w-full"
            >
              <BrowserStep
                title={step.description}
                stepType={getStepTypeFromAction(step.action_type)}
                status={status}
                time={getStepTime(step)}
                showLoader={status === EBrowserStepStatus.CURRENT && !isReplay}
                onClick={
                  seekable && onStepClick ? () => onStepClick(step) : undefined
                }
              />
            </div>
          );
        })}
      </Flex>
    </ScrollArea>
  );
}
